import { config } from '@/config';

class ApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = config.apiUrl;
  }

  private getToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  private async request<T>(
    path: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = this.getToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...((options.headers as Record<string, string>) || {}),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseUrl}${path}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    if (response.status === 204) return undefined as T;
    return response.json();
  }

  // Public endpoints
  async getEventBySlug(slug: string, password?: string) {
    const headers: Record<string, string> = {};
    if (password) headers['X-Event-Password'] = password;
    return this.request<any>(`/events/${slug}`, { headers });
  }

  async verifyPassword(slug: string, password: string) {
    return this.request<{ valid: boolean }>(`/events/${slug}/verify-password`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
  }

  async submitRSVP(slug: string, data: any) {
    return this.request<any>(`/events/${slug}/rsvp`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Authenticated endpoints
  async createEvent(data: any) {
    return this.request<any>('/admin/events', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async listMyEvents() {
    return this.request<any[]>('/admin/events');
  }

  async getEvent(id: string) {
    return this.request<any>(`/admin/events/${id}`);
  }

  async updateEvent(id: string, data: any) {
    return this.request<any>(`/admin/events/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteEvent(id: string) {
    return this.request<void>(`/admin/events/${id}`, { method: 'DELETE' });
  }

  async listRSVPs(eventId: string) {
    return this.request<any[]>(`/admin/events/${eventId}/rsvps`);
  }

  async getUploadUrl(eventId: string, filename: string, contentType: string) {
    return this.request<{ upload_url: string; file_url: string }>(
      `/admin/events/${eventId}/upload-url`,
      {
        method: 'POST',
        body: JSON.stringify({ filename, content_type: contentType }),
      }
    );
  }

  async uploadFile(presignedUrl: string, file: File) {
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type },
    });
    if (!response.ok) throw new Error('Upload failed');
  }
}

export const api = new ApiService();
